import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileContainer, mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";

import {
    getEntityAsync,
    getEntitiesAsync,
    createEntityAsync,
    updateEntityAsync,
    deleteEntityAsync,
    UpdateEntity,
    CreateEntity,
    uploadEntityFileAsync,
    getEntitiesByClientIdAsync,
    getEntitiesForUserAsync,
} from "./entityApi";
import { queryKeys } from "../queryKeys";

export function useGetEntities() {
    return useQuery({ ...queryKeys.entities.all, queryFn: getEntitiesAsync });
}

export function useGetEntitiesForUser() {
    return useQuery({ ...queryKeys.entities.users, queryFn: getEntitiesForUserAsync });
}

export function useGetEntitiesByClientId(clientId: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.entities.byClientId(clientId || ""),
        queryFn: async () => {
            const entities = await getEntitiesByClientIdAsync(clientId || "")
                .then(entities => {
                    return entities;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the entities information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return entities;
        },
        enabled: !!clientId,
    });
}

export const useGetEntity = (id: string | undefined) => {
    const toast = useToast();
    return useQuery({
        ...queryKeys.entities.byId(id || ""),
        queryFn: async () => {
            const entity = await getEntityAsync(id || "")
                .then(entity => {
                    return entity;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the entity information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return entity;
        },
        enabled: !!id,
    });
};

export function useUpdateEntity() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: UpdateEntity) => {
            return await updateEntityAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.entities.all);
            toast({
                title: "Updated",
                description: "Entity updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update entity"),
    });
}

export function useCreateEntity() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: CreateEntity) => {
            return await createEntityAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.entities.all);
            toast({
                title: "Created",
                description: "Entity created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to create entity"),
    });
}

export function useDeleteEntity() {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: { id: string; clientId: string }) => {
            return await deleteEntityAsync(args.id);
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries(queryKeys.entities.all);
            await queryClient.invalidateQueries(queryKeys.transactions.all);
            await queryClient.invalidateQueries(queryKeys.transactions.byClientId(variables.clientId));
            await queryClient.invalidateQueries(queryKeys.reports.all);
            await queryClient.invalidateQueries(queryKeys.reports.byClientId(variables.clientId));
            toast({
                title: "Deleted",
                description: "Entity deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete entity"),
    });
}

export function useBulkUploadEntity() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer; clientId: string }) => {
            await uploadEntityFileAsync(args.file, args.clientId);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.entities.all);
            toast({
                title: "Uploaded",
                description: "Entity file uploaded successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to upload entity file. Check that all rows/fields are filled out appropriately."),
    });
}
